import { accountMultipleDiagnoses, enrollmentDeviceInput } from './enrollmentSettings';

import type { DeviceLike, ResolvedEnrollmentSettings } from './enrollmentSettings';
import type { EnrollmentChecklistInputs } from './evaluateEnrollmentChecklist';
import type { EnrollmentRecord } from '@/types/enrollment';
import type { InsurancePolicy } from '@/types/insurance';
import type { Organization } from '@/types/organization';
import type { Patient } from '@/types/patient';

/** Patient fields the CPE checklist edits in place. Shown at once, reverted if the save fails. */
export interface EnrollmentPatientOverrides {
  departmentId?: string | null;
  practitionerId?: string | null;
  practitionerName?: string | null;
  mrn2?: string;
  oac?: string;
  isHfMonitored?: boolean;
  diagnosisOption?: string;
  financialPayerOverride?: string | null;
}

/** Codes and primary flag from the pages of `useGetPatientDiagnosesInfiniteQuery`. */
export function diagnosisFactsFromPages(
  pages:
    | { data?: { diagnosisCode?: string | null; isPrimary?: boolean | null }[] }[]
    | null
    | undefined
): { codes: string[]; hasPrimary: boolean } {
  const rows = (pages ?? []).flatMap((page) => page?.data ?? []);
  const codes = rows
    .map((row) => row.diagnosisCode)
    .filter((code): code is string => Boolean(code));
  return {
    codes,
    hasPrimary: rows.some((row) => row.isPrimary === true && Boolean(row.diagnosisCode))
  };
}

/** Codes from the pages of `useGetPatientDiagnosesInfiniteQuery`. */
export function diagnosisCodesFromPages(
  pages: { data?: { diagnosisCode?: string | null }[] }[] | null | undefined
): string[] {
  return diagnosisFactsFromPages(pages).codes;
}

export interface BuildEnrollmentChecklistInputsArgs {
  patient: Patient | undefined;
  clinic: Organization | undefined;
  clinicId: string;
  departments: { id: string; name: string }[];
  soleDepartment: { id: string; name: string } | null;
  diagnosisCodes: string[];
  /** One of `diagnosisCodes` is marked primary. Omit when the caller did not load that flag. */
  diagnosisHasPrimary?: boolean;
  /** The device this enrollment is for (active, else newest inactive). */
  device: DeviceLike | null;
  /** The active device, when there is one. */
  activeDevice: DeviceLike | null;
  primaryPolicy: InsurancePolicy | null;
  enrollment: EnrollmentRecord | null;
  billingType: string | null;
  ehrIntegrated: boolean;
  hfMonitoring: { enabled: boolean; manufacturers: string[] | null };
  settings: ResolvedEnrollmentSettings;
  overrides?: EnrollmentPatientOverrides;
  /** Vendor key → display name (e.g. `boston_scientific` → `Boston Scientific`). */
  vendorName?: (vendor: string) => string;
}

/**
 * Builds the checklist inputs for one patient. Pure, so the CPE task page and the patient list
 * evaluate the same way. `overrides` are the CPE page's in-place edits; pass none elsewhere.
 */
export function buildEnrollmentChecklistInputs({
  patient,
  clinic,
  clinicId,
  departments,
  soleDepartment,
  diagnosisCodes,
  diagnosisHasPrimary,
  device,
  activeDevice,
  primaryPolicy,
  enrollment,
  billingType,
  ehrIntegrated,
  hfMonitoring,
  settings,
  overrides = {},
  vendorName
}: BuildEnrollmentChecklistInputsArgs): EnrollmentChecklistInputs {
  const departmentId = 'departmentId' in overrides ? overrides.departmentId : patient?.departmentId;
  const practitionerId =
    'practitionerId' in overrides ? overrides.practitionerId : patient?.practitioner?.id;
  const practitionerName =
    'practitionerName' in overrides
      ? overrides.practitionerName
      : [patient?.practitioner?.given, patient?.practitioner?.family].filter(Boolean).join(' ');
  const departmentName =
    departments.find((row) => row.id === departmentId)?.name ??
    (patient?.department?.id === departmentId ? patient?.department?.name : null);
  const deviceInput = enrollmentDeviceInput(device, vendorName);
  return {
    patient: {
      givenName: patient?.givenName,
      familyName: patient?.familyName,
      birthDate: patient?.birthDate,
      sex: patient?.sex,
      phones: [patient?.phoneNumber, patient?.homePhone, patient?.otherPhone],
      address: patient?.address ?? null,
      clinicId: patient?.clinic?.id || clinicId,
      clinicName: patient?.clinic?.name || clinic?.name,
      departmentId,
      departmentName,
      practitionerId,
      practitionerName,
      mrn2: overrides.mrn2 ?? patient?.mrn2,
      diagnosisCodes,
      diagnosisHasPrimary,
      defaultDiagnosisCode:
        overrides.diagnosisOption ?? (patient?.icdDiagnosis?.code || patient?.diagnosisOption),
      oac: overrides.oac ?? patient?.oac,
      isHfMonitored: overrides.isHfMonitored ?? patient?.settings?.isHfMonitored ?? null,
      isRemoteMonitored: patient?.isRemoteMonitored
    },
    device: deviceInput ? { ...deviceInput, active: Boolean(activeDevice) } : null,
    insurance: {
      primary: primaryPolicy
        ? { payerName: primaryPolicy.payerName, memberId: primaryPolicy.subscriberId }
        : null,
      financialOverride:
        'financialPayerOverride' in overrides
          ? overrides.financialPayerOverride
          : patient?.financialPayerOverride
    },
    enrollment,
    account: {
      billingType,
      ehrIntegrated,
      multipleDiagnoses: accountMultipleDiagnoses(clinic),
      departmentsAvailable: departments.length > 0,
      soleDepartment,
      hfMonitoringEnabled: hfMonitoring.enabled,
      hfManufacturers: hfMonitoring.manufacturers
    },
    settings: {
      enrollmentChecklist: settings.enrollmentChecklist,
      ffsDeviceTypes: settings.ffsDeviceTypes
    }
  };
}
